import { createTaskByTool, getTaskDetail } from '@/app/apis';
import type { OfflineAvatarInput, TaskInfo, TaskParams } from '@/app/types';
import { ceilVideoDurationToSeconds, estimateLipSyncTextDurationSeconds } from '@/app/utils';

export interface CreateOfflineAvatarTaskOptions {
  avatarId?: string;
  useTrial?: boolean;
}

export interface TtsResult {
  uri: string;
  duration: number;
}

export class TtsGenerationError extends Error {
  readonly code?: number;
  readonly reason?: string;
  readonly metadata?: Record<string, string>;

  constructor(reason?: string, message?: string, metadata?: Record<string, string>, code?: number) {
    super(message || reason || 'TTS generation failed');
    this.name = 'TtsGenerationError';
    this.code = code;
    this.reason = reason;
    this.metadata = metadata;
  }
}

export class OfflineAvatarTaskError extends Error {
  readonly code?: number;
  readonly reason?: string;
  readonly metadata?: Record<string, string>;

  constructor(reason?: string, message?: string, metadata?: Record<string, string>, code?: number) {
    super(message || reason || 'Offline avatar task submission failed');
    this.name = 'OfflineAvatarTaskError';
    this.code = code;
    this.reason = reason;
    this.metadata = metadata;
  }
}

export function buildTtsTaskParams(script: string, voiceId: string): TaskParams {
  return {
    type: 'tts_free',
    input: {
      enhance: true,
      prompts: [{ type: 'text', content: script.trim(), audio: { pitch: 0 } }],
    },
    settings: { voice_id: voiceId, speed: 1, volume: 0 },
  };
}

function getTtsResult(task: TaskInfo): TtsResult | null {
  const creation = task.creations?.[0];
  return creation?.uri && creation.duration ? { uri: creation.uri, duration: creation.duration } : null;
}

export async function createTtsTask(script: string, voiceId: string): Promise<TaskInfo> {
  const { data, error } = await createTaskByTool({ body: buildTtsTaskParams(script, voiceId) });
  if (error || !data) throw new TtsGenerationError(error?.reason, error?.message, error?.metadata, error?.code);
  return data;
}

export async function getTtsTaskResult(taskId: string): Promise<TtsResult> {
  const detail = await getTaskDetail({ path: { id: taskId } });
  if (detail.error || !detail.data) {
    throw new TtsGenerationError(
      detail.error?.reason,
      detail.error?.message,
      detail.error?.metadata,
      detail.error?.code,
    );
  }

  if (detail.data.state === 'failed' || detail.data.state === 'canceled') {
    throw new TtsGenerationError(detail.data.err_code, detail.data.err_msg);
  }

  const result = getTtsResult(detail.data);
  if (!result) throw new TtsGenerationError(undefined, 'TTS task succeeded without an audio result');
  return result;
}

export function buildOfflineAvatarTaskParams(
  input: OfflineAvatarInput,
  { avatarId, useTrial = false }: CreateOfflineAvatarTaskOptions = {},
): TaskParams {
  const prompts: TaskParams['input']['prompts'] = [
    avatarId
      ? { type: 'offline_avatar', content: '', reference: { offline_avatar_id: avatarId } }
      : { type: 'image', content: input.image, src_imgs: [input.image], name: '图片1' },
    input.speech.mode === 'text'
      ? { type: 'text', content: input.speech.text.trim() }
      : { type: 'audio', content: input.speech.audio, name: '音频1' },
  ];

  if (input.actions?.length) {
    prompts.push({
      type: 'timeline',
      content: '',
      offline_avatar_timeline: {
        actions: input.actions.map(({ start, end, prompt }) => ({ text: prompt.trim(), start, end })),
      },
    });
  }

  const duration =
    input.speech.mode === 'text'
      ? estimateLipSyncTextDurationSeconds(input.speech.text)
      : Math.max(ceilVideoDurationToSeconds(input.speech.duration), 5);

  return {
    type: 'offline_avatar',
    input: { enhance: true, prompts },
    settings: {
      resolution: input.resolution ?? '720p',
      sample_count: 1,
      duration,
      use_trial: useTrial,
      ...(input.speech.mode === 'text' && input.speech.voice ? { voice_id: input.speech.voice } : {}),
    },
  };
}

export async function createOfflineAvatarTask(
  input: OfflineAvatarInput,
  options?: CreateOfflineAvatarTaskOptions,
): Promise<TaskInfo> {
  const { data, error } = await createTaskByTool({ body: buildOfflineAvatarTaskParams(input, options) });
  if (error || !data) {
    throw new OfflineAvatarTaskError(error?.reason, error?.message, error?.metadata, error?.code);
  }
  return data;
}
